/**
 * The world's models from `packages/assets/models/world.glb`, exported from world.blend by
 * `3d/export-gltf.py`. Every model becomes one merged geometry with two extra vertex attributes
 * that carry the part conventions of the atlas pipeline (packages/assets/README.md):
 *
 * - `aFixed`: the flat tone of a part, or -1 for a part shaded by the sun. `lit-` parts are paper,
 *   `ink-` parts are ink, `grey-NN` parts are the tone NN percent, `accent-` parts, -2, are
 *   drawn in the accent role (a banner's cloth), `glow-` parts, -3, are a house's interior
 *   seen through its openings, dark by day, and `hem-` parts, -4, are shaded by the sun and move
 *   with a banner's cloth, rolled up and unrolled with it and waved with its hem: c4's rod.
 * - `aGroup`: the part's outline group within the model, from 1. A line is drawn where two groups
 *   meet, as render.py does with its id pass.
 *
 * A tile (x, y) of the game is at (x, height, y) in the file, one unit per tile.
 */
import { familyOf } from "@mistsweeper/assets";
import { BufferAttribute, type BufferGeometry, type Mesh, type Object3D, Vector3 } from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import type { Box } from "./touch.ts";

/** The tone bands a model is shaded in, by its family (catalog `tones`). */
export type Bands = "plain" | "tree" | "crop";

export interface Model {
  readonly geometry: BufferGeometry;
  /**
   * The sun's tone bands: conifers the darker ones; birches and bushes, light among them, the
   * plain ones; a crop the lighter ones, ripe grain.
   */
  readonly bands: Bands;
  /**
   * How it moves, named by the model itself (decision 0019): spin, nod, pennant, soar, dabble,
   * or null. The collection's custom property in world.blend, carried by the glTF extras.
   */
  readonly motion: string | null;
  /** A rider: the probe it sits at on the model its name extends, as `iso-windmill-1-sails` on `iso-windmill-1`. */
  readonly rides: string | null;
  /** The box round it in tiles, as `spec/vectors/models.json` pins it: what a touch tests. */
  readonly box: Box;
}

export interface ModelSet {
  readonly models: ReadonlyMap<string, Model>;
  /** Named points, by model and probe name, in tile units from the model's anchor. */
  readonly probes: ReadonlyMap<string, Vector3>;
}

function fixedTone(name: string): number {
  if (name.startsWith("accent-")) return -2;
  if (name.startsWith("glow-")) return -3;
  if (name.startsWith("hem-")) return -4;
  if (name.startsWith("lit-")) return 1;
  if (name.startsWith("ink-")) return 0;
  const grey = /^grey-(\d+)/.exec(name);
  if (grey) return Number(grey[1]) / 100;
  return -1;
}

function modelGeometry(root: Object3D): BufferGeometry | null {
  const parts: BufferGeometry[] = [];
  const groups = new Map<string, number>();
  root.updateWorldMatrix(true, true);
  root.traverse((node) => {
    const mesh = node as Mesh;
    if (!mesh.isMesh) return;
    let geometry = mesh.geometry.clone();
    for (const name of Object.keys(geometry.attributes)) {
      if (name !== "position" && name !== "normal") geometry.deleteAttribute(name);
    }
    if (geometry.index) geometry = geometry.toNonIndexed();
    geometry.applyMatrix4(mesh.matrixWorld);
    // The node name has its dots removed by the loader; the group travels in the extras.
    const name = mesh.name || mesh.parent?.name || "";
    const groupName = String(mesh.userData.group ?? mesh.parent?.userData.group ?? name);
    let group = groups.get(groupName);
    if (group === undefined) {
      group = groups.size + 1;
      groups.set(groupName, group);
    }
    const count = geometry.attributes.position?.count ?? 0;
    geometry.setAttribute(
      "aFixed",
      new BufferAttribute(new Float32Array(count).fill(fixedTone(name)), 1),
    );
    geometry.setAttribute("aGroup", new BufferAttribute(new Float32Array(count).fill(group), 1));
    parts.push(geometry);
  });
  if (parts.length === 0) return null;
  return mergeGeometries(parts, false);
}

/** The box round a geometry, in tiles. */
function boxOf(geometry: BufferGeometry): Box {
  geometry.computeBoundingBox();
  const box = geometry.boundingBox;
  if (!box) throw new Error("a model has no box");
  return { min: [box.min.x, box.min.y, box.min.z], max: [box.max.x, box.max.y, box.max.z] };
}

export async function loadModels(url: string): Promise<ModelSet> {
  return modelsFrom(await new GLTFLoader().loadAsync(url));
}

/**
 * The models of a loaded glTF file: every root node named `iso-...` with a mesh under it, and the
 * probes of every model. `spec/vectors/models.json` pins what this reads (decision 0020), so
 * another renderer reads the file alike.
 */
export function modelsFrom(gltf: { readonly scene: Object3D }): ModelSet {
  const models = new Map<string, Model>();
  const probes = new Map<string, Vector3>();
  for (const root of gltf.scene.children) {
    const name = root.name;
    if (!name.startsWith("iso-")) continue;
    const geometry = modelGeometry(root);
    if (geometry) {
      models.set(name, {
        geometry,
        bands: familyOf(name)?.tones ?? "plain",
        motion: typeof root.userData.motion === "string" ? root.userData.motion : null,
        rides: typeof root.userData.rides === "string" ? root.userData.rides : null,
        box: boxOf(geometry),
      });
    }
    root.updateWorldMatrix(true, true);
    root.traverse((node) => {
      if (node.name.startsWith("probe-")) {
        // Blender names are unique across the file, so a probe may carry a number: chimney-top001.
        const probe = node.name.slice(6).replace(/\d+$/, "");
        probes.set(`${name}/${probe}`, node.getWorldPosition(new Vector3()));
      }
    });
  }
  return { models, probes };
}
